import { debounce } from 'lodash';
import api from 'mastodon/api';
import { me } from 'mastodon/initial_state';

const MAX_STORED_VIEWS = 1000;
const BATCH_SIZE = 100;
const DEBOUNCE_DELAY = 1000;
const RETRY_DELAY = 5000;
const DEVICE_ID_KEY = 'mastodon_device_id';
const VIEWED_STATUSES_KEY_PREFIX = 'viewedStatuses_';

export const viewedStatusesMap = new Map();
const pendingViewsMap = new Map();
export const sessionViewedStatusesMap = new Map();
const failedAttempts = { count: 0 };

function generateDeviceId() {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    navigator.platform,
  ].join('|');

  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    hash = ((hash << 5) - hash) + fingerprint.charCodeAt(i);
    hash |= 0;
  }
  return 'fp_social_laoapp' + Math.abs(hash).toString(36);
}

function saveDeviceId(deviceId) {
  if (!deviceId) return;

  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    document.cookie = `${DEVICE_ID_KEY}=${deviceId}; max-age=${30 * 24 * 60 * 60}; path=/; SameSite=Strict`;
  } catch (e) {
    console.error('Error saving deviceId:', e);
  }
}

function getDeviceId() {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [key, value] = cookie.trim().split('=');
      if (key === DEVICE_ID_KEY) {
        return value;
      }
    }

    const storedId = localStorage.getItem(DEVICE_ID_KEY);
    if (storedId) {
      return storedId;
    }

    const newDeviceId = generateDeviceId();
    saveDeviceId(newDeviceId);
    return newDeviceId;
  } catch (e) {
    console.error('Error accessing deviceId:', e);
    return generateDeviceId();
  }
}

export function getAccountId() {
  try {
    if (me) {
      return me;
    }
    return getDeviceId();
  } catch (e) {
    console.error('Error getting account ID:', e);
    return getDeviceId();
  }
}

function getStoredViewedStatuses(accountId) {
  try {
    const key = `${VIEWED_STATUSES_KEY_PREFIX}${accountId}`;
    const stored = JSON.parse(localStorage.getItem(key) || '[]').map(String);
    return stored.slice(-MAX_STORED_VIEWS).filter(id => /^\d+$/.test(id));
  } catch (e) {
    console.error(`Error parsing viewedStatuses for account ${accountId} from localStorage:`, e);
    return [];
  }
}

export function initializeAccountSets(accountId) {
  if (!viewedStatusesMap.has(accountId)) {
    viewedStatusesMap.set(accountId, new Set(getStoredViewedStatuses(accountId)));
    pendingViewsMap.set(accountId, new Set());
    sessionViewedStatusesMap.set(accountId, new Set());
  }
}

function saveViewedStatusesToLocalStorage(accountId) {
  try {
    const key = `${VIEWED_STATUSES_KEY_PREFIX}${accountId}`;
    const viewedArray = [...viewedStatusesMap.get(accountId)].filter(id => /^\d+$/.test(id));
    const data = JSON.stringify(viewedArray.slice(-MAX_STORED_VIEWS));
    if (data.length * 2 > 5 * 1024 * 1024) {
      console.warn(`localStorage limit approaching for account ${accountId}`);
      return;
    }
    localStorage.setItem(key, data);
  } catch (e) {
    console.error(`Error saving viewedStatuses for account ${accountId} to localStorage:`, e);
  }
}

export function recordView(statusId, dispatch = null) {
  if (!/^\d+$/.test(statusId)) {
    return;
  }

  const accountId = getAccountId();
  initializeAccountSets(accountId);

  const sessionViewedStatuses = sessionViewedStatusesMap.get(accountId);
  if (sessionViewedStatuses.has(statusId)) {
    return;
  }

  sessionViewedStatuses.add(statusId);

  const viewedStatuses = viewedStatusesMap.get(accountId);
  if (viewedStatuses.has(statusId)) {
    return;
  }

  viewedStatuses.add(statusId);
  pendingViewsMap.get(accountId).add(statusId);

  saveViewedStatusesToLocalStorage(accountId);

  if (dispatch) {
    dispatch({
      type: 'STATUS_VIEW_COUNT_UPDATE',
      statusId: statusId,
    });
  }

  if (pendingViewsMap.get(accountId).size >= BATCH_SIZE) {
    sendViews(accountId);
  } else {
    triggerSendViews(accountId);
  }
}

async function sendViews(accountId) {
  const pendingViews = pendingViewsMap.get(accountId);
  if (!pendingViews || pendingViews.size === 0) return;

  const batchToSend = [...pendingViews].slice(0, BATCH_SIZE);
  batchToSend.forEach(id => pendingViews.delete(id));

  const isValidBatch = batchToSend.every(id => /^\d+$/.test(id));
  if (!isValidBatch) {
    batchToSend.forEach(id => pendingViews.add(id));
    retryWithBackoff(accountId);
    return;
  }

  try {
    const options = {};
    const deviceId = getDeviceId();

    const response = await api().post('/api/v1/status_views/batch', {
      status_ids: batchToSend,
      device_id: deviceId,
      account_id: accountId,
    }, options);

    if (response) {
      const responseDeviceId = response.headers?.get('x-device-id');
      if (responseDeviceId) {
        saveDeviceId(responseDeviceId);
      }

      if (response.data && Array.isArray(response.data.processed)) {
        if (response.data.processed.length > 0) {
          response.data.processed.forEach(id => pendingViews.delete(String(id)));
        }

        if (response.data.processed.length > 0) {
          failedAttempts.count = 0;
        }

        if (pendingViews.size > 0) {
          setTimeout(() => sendViews(accountId), 1000);
        }
      } else {
        console.warn('sendViews: Server returned unexpected response format:', response.data);
        batchToSend.forEach(id => pendingViews.add(id));
        retryWithBackoff(accountId);
      }
    } else {
      console.warn(`sendViews: API failed with status ${response.status}: ${response.statusText}`);
      retryWithBackoff(accountId);
    }
  } catch (error) {
    console.error('sendViews: Error sending views:', error);
    retryWithBackoff(accountId);
  }
}

function retryWithBackoff(accountId) {
  if (failedAttempts.count >= 5) {
    console.error('Max retry attempts reached, clearing pending views for account', accountId);
    pendingViewsMap.get(accountId).clear();
    failedAttempts.count = 0;
    return;
  }
  failedAttempts.count += 1;
  const backoffDelay = Math.min(RETRY_DELAY * Math.pow(1.5, failedAttempts.count - 1), 60000);
  setTimeout(() => sendViews(accountId), backoffDelay);
}

const triggerSendViews = debounce((accountId) => sendViews(accountId), DEBOUNCE_DELAY);

window.addEventListener('beforeunload', () => {
  const accountId = getAccountId();
  saveViewedStatusesToLocalStorage(accountId);
  const pendingViews = pendingViewsMap.get(accountId) || new Set();
  if (pendingViews.size > 0) {
    const viewsToSend = [...pendingViews].slice(0, BATCH_SIZE).filter(id => /^\d+$/.test(id));
    const deviceId = getDeviceId();

    if (navigator.sendBeacon) {
      const data = { status_ids: viewsToSend.map(Number), device_id: deviceId, account_id: accountId };
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      navigator.sendBeacon('/api/v1/status_views/batch', blob);
    } else {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/v1/status_views/batch', false);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(JSON.stringify({ status_ids: viewsToSend.map(Number), device_id: deviceId, account_id: accountId }));
      } catch (e) {
        console.error('Error sending views on page unload:', e);
      }
    }
  }
});